import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from '@mui/material'
import { FormType } from '../utils/sharedTypes'
import { addPrefixSpecifier } from '../utils/misc'
import { funders } from '../utils/constants'
import { MdExpandMore } from 'react-icons/md'
import FundingSummaryAlma from './FundingSummaryAlma'
import FundingSummaryFIFPL from './FundingSummaryFIFPL'
import FundingSummaryDPC from './FundingSummaryDPC'
import FundingSummaryBankTransfer from './FundingSummaryBankTransfer'
import FundingSummaryDPCPreRegistration from './FundingSummaryDPCPreRegistration'
import FundingSummaryOPCO from './FundingSummaryOPCO'
import {
  getCatalogPrice,
  getDpcProductPartOrga,
  getDpcProductPrice,
  getGenericProductPrice,
  getListingPrice,
  getOpcoProductPrice,
  hasMultipleProducts,
  hasPriceDiscount,
} from '../utils/pricing'

type Props = {
  form: FormType
}

export default function FundingSummary({ form }: Props) {
  const hasProducts = hasMultipleProducts(form.data)

  // Actual price charged to the user (post-discount, from the registration)
  const listing_price = getListingPrice(form.data)

  // Catalog (list) price before any discount
  const catalog_price = getCatalogPrice(form.data)

  const hasDiscount = hasPriceDiscount(form.data)

  return (
    <Grid item>
      {form.data.funding_organism?.code === 'ALMA' ? (
        <FundingSummaryAlma form={form} />
      ) : form.data.funding_organism?.code === 'BANK_TRANSFER' ? (
        <FundingSummaryBankTransfer form={form} />
      ) : ['FIFPL', 'FAFPM'].includes(form.data.funding_organism?.code ?? '') ? (
        <FundingSummaryFIFPL form={form} />
      ) : form.data.funding_organism?.code === 'DPC' ? (
        form.data.pre_registration ? (
          <FundingSummaryDPCPreRegistration form={form} />
        ) : (
          <FundingSummaryDPC form={form} />
        )
      ) : form.data.funding_organism?.type === 'opco' ? (
        <FundingSummaryOPCO form={form} />
      ) : (
        <Grid item container direction="row" justifyContent="space-between" wrap="nowrap" alignItems="flex-end">
          <Grid item>
            <Typography variant="body2">Prix de la formation (TTC)</Typography>
          </Grid>
          <Grid item sx={{ textAlign: 'right' }}>
            {hasDiscount && (
              <Typography variant="body2" sx={{ textDecorationLine: 'line-through', color: 'text.secondary' }}>
                {Intl.NumberFormat('fr-FR', {
                  style: 'currency',
                  currency: 'EUR',
                  maximumFractionDigits: 0,
                }).format(catalog_price)}
              </Typography>
            )}
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'EUR',
                maximumFractionDigits: 0,
              }).format(listing_price) || '-'}
            </Typography>
          </Grid>
        </Grid>
      )}

      {hasProducts && (
        <Grid item mt={3}>
          <Accordion>
            <AccordionSummary expandIcon={<MdExpandMore />} aria-controls="panel2-content" id="panel2-header">
              <Typography variant="body2">Voir le détail de vos inscriptions</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid item container direction="column" justifyContent="space-between" rowSpacing={1}>
                {form.data.products.map((product) => {
                  if (form.data.funding_organism?.code == 'DPC') {
                    const funding_organism_registration = form.data.funding_organism_registrations.find(
                      (r) => r.product.product_code === product.product_code && r.cancelled === false,
                    )
                    const product_catalog = form.data.product_catalogs.find(
                      (pc) => pc.product == product.id && pc.funding_organism == form.data.funding_organism?.code,
                    )

                    if (!funding_organism_registration && !product_catalog) {
                      return null
                    }

                    const productPrice = getDpcProductPrice(form.data, product)
                    const partOrga = getDpcProductPartOrga(form.data, product)

                    return (
                      <Grid item container direction="column" justifyContent="space-between" key={product.product_code}>
                        <Typography variant="body1" fontWeight="500">
                          {product.name}
                        </Typography>
                        <Grid item container direction="row" justifyContent="space-between" wrap="nowrap">
                          <Grid item>
                            <Typography variant="body2">Prix de la formation</Typography>
                          </Grid>
                          <Typography variant="body1" sx={{ textAlign: 'right', fontWeight: 500 }}>
                            {Intl.NumberFormat('fr-FR', {
                              style: 'currency',
                              currency: 'EUR',
                              maximumFractionDigits: 2,
                            }).format(productPrice) || '-'}
                          </Typography>
                        </Grid>
                        <Grid item container direction="row" justifyContent="space-between" wrap="nowrap">
                          <Grid item>
                            <Typography variant="body2">
                              Prise en charge par{' '}
                              {addPrefixSpecifier(
                                funders.find((p) => p.code === form.data.funding_organism?.code)?.label,
                              ) || '-'}
                            </Typography>
                            <Typography variant="caption">
                              Cette prise en charge est conditionnée à la validation de la formation
                            </Typography>
                          </Grid>
                          <Typography variant="body1" sx={{ textAlign: 'right', fontWeight: 500 }}>
                            {Intl.NumberFormat('fr-FR', {
                              style: 'currency',
                              currency: 'EUR',
                              maximumFractionDigits: 2,
                            }).format(partOrga) || '-'}
                          </Typography>
                        </Grid>
                      </Grid>
                    )
                  } else if (form.data.funding_organism?.type === 'opco') {
                    const line_item = form.data.estimate?.line_items.find((li) => li.product_id === product.id)
                    return (
                      <Grid item container direction="column" justifyContent="space-between">
                        <Typography variant="body1" fontWeight="500">
                          {product.name}
                        </Typography>
                        <Grid item container direction="row" justifyContent="space-between" wrap="nowrap">
                          <Grid item>
                            <Typography variant="body2">Prix de la formation</Typography>
                          </Grid>
                          <Typography variant="body1" sx={{ textAlign: 'right', fontWeight: 500 }}>
                            {line_item
                              ? Intl.NumberFormat('fr-FR', {
                                  style: 'currency',
                                  currency: 'EUR',
                                  maximumFractionDigits: 2,
                                }).format(getOpcoProductPrice(form.data, product))
                              : '-'}
                          </Typography>
                        </Grid>
                        {/* <Grid item container direction="row" justifyContent="space-between" wrap="nowrap">
                          <Grid item>
                            <Typography variant="body2">Reste à charge</Typography>
                          </Grid>
                          <Typography variant="body1" sx={{ textAlign: 'right', fontWeight: 500 }}>
                            {Intl.NumberFormat('fr-FR', {
                              style: 'currency',
                              currency: 'EUR',
                              maximumFractionDigits: 2,
                            }).format(funding_organism_registration?.default_personal_funding) || '-'}
                          </Typography>
                        </Grid> */}
                      </Grid>
                    )
                  } else {
                    const actualPrice = getGenericProductPrice(form.data, product)
                    if (actualPrice === null) return null

                    const catalogProductPrice = product.listing_price
                    const productHasDiscount = !!catalogProductPrice && catalogProductPrice > actualPrice
                    return (
                      <Grid item container direction="column" justifyContent="space-between" key={product.product_code}>
                        <Typography variant="body1" fontWeight="500">
                          {product.name}
                        </Typography>
                        <Grid item container direction="row" justifyContent="space-between" wrap="nowrap" alignItems="flex-end">
                          <Grid item>
                            <Typography variant="body2">Prix de la formation</Typography>
                          </Grid>
                          <Grid item sx={{ textAlign: 'right' }}>
                            {productHasDiscount && (
                              <Typography variant="body2" sx={{ textDecorationLine: 'line-through', color: 'text.secondary' }}>
                                {Intl.NumberFormat('fr-FR', {
                                  style: 'currency',
                                  currency: 'EUR',
                                  maximumFractionDigits: 2,
                                }).format(catalogProductPrice)}
                              </Typography>
                            )}
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {Intl.NumberFormat('fr-FR', {
                                style: 'currency',
                                currency: 'EUR',
                                maximumFractionDigits: 2,
                              }).format(actualPrice) || '-'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    )
                  }
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
    </Grid>
  )
}
